import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import Navbar from "../components/NavBar";
import { useAppDispatch, useAppSelector } from "../store/store";
import PostAction from "../store/post/action";
import OrderAction from "../store/order/action";
import Clothes from "../models/clothes/clothes.model";
import { ClothesTypes, ClothesSize, ClothesColors, ClothesGender } from "../models/clothes/clothes.model";
import { categoryLabels, categoryList } from "../utils/categoryData";
import { sizeOptions, colorOptions, colorLabels, colorHex } from "../utils/filterOptions";

const MyPostEditPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const item = location.state?.item as Clothes | undefined;
    const { user } = useAppSelector((s) => s.auth);
    const { updatePostSuccess, deletePostSuccess, isLoading, error } = useAppSelector((s) => s.post);
    const { orders } = useAppSelector((s) => s.order);

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [type, setType] = useState<ClothesTypes>("dress");
    const [sizes, setSizes] = useState<ClothesSize[]>(["M"]);
    const [colors, setColors] = useState<ClothesColors[]>(["black"]);
    const [gender, setGender] = useState<ClothesGender>("female");
    const [sellPrice, setSellPrice] = useState(0);
    const [rentPrice, setRentPrice] = useState(0);
    const [isFree, setIsFree] = useState(false);
    const [isTransportXK, setIsTransportXK] = useState(true);
    const [isTransportMK, setIsTransportMK] = useState(false);
    const [isTransportAL, setIsTransportAL] = useState(false);

    useEffect(() => {
        if (user?.id && orders.length === 0) {
            OrderAction.getOrders(user.id, moment().month(), moment().year(), dispatch);
        }
    }, [user?.id, orders.length, dispatch]);

    useEffect(() => {
        if (item) {
            setName(item.name);
            setDescription(item.description || "");
            setType(item.type);
            setSizes(item.size?.length ? item.size : ["M"]);
            setColors(item.colors?.length ? item.colors : ["black"]);
            setGender(item.gender || "female");
            setSellPrice(item.prices?.sellPrice ?? 0);
            setRentPrice(item.prices?.rentPrice ?? 0);
            setIsFree(item.prices?.isFree ?? false);
            setIsTransportXK(item.isTransportXKIncluded ?? true);
            setIsTransportMK(item.isTransportMKIncluded ?? false);
            setIsTransportAL(item.isTransportALIncluded ?? false);
        }
    }, [item]);

    const toggleSize = (s: ClothesSize) => {
        setSizes((prev) => {
            const next = prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s];
            return next.length ? next : ["M"];
        });
    };

    const toggleColor = (c: ClothesColors) => {
        setColors((prev) =>
            prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
        );
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
        // if (!item) return;
        // const fiftyDaysHavePassed = moment().valueOf() > moment(item.created_at).add(50, "days").valueOf();
        // const deliveredOrders = orders.filter((o) => o.product?.id === item.id && o.status === "delivered");
        // if (!fiftyDaysHavePassed && deliveredOrders.length === 0) {
        //     setShowDeleteWarning(true);
        // } else {
        //     setShowDeleteConfirm(true);
        // }
    };

    const handleDeleteConfirm = () => {
        if (item) {
            if (user) PostAction.deletePost(user, item, dispatch);
            setShowDeleteConfirm(false);
        }
    };

    const handleSubmit = () => {
        if (!user?.id || !item || !name.trim()) return;
        const updated: Clothes = {
            ...item,
            name: name.trim(),
            description: description.trim(),
            type,
            size: sizes,
            colors,
            gender,
            images: item.images ?? [],
            prices: {
                sellPrice: isFree ? 0 : sellPrice,
                rentPrice,
                isFree,
            },
            isTransportXKIncluded: isTransportXK,
            isTransportMKIncluded: isTransportMK,
            isTransportALIncluded: isTransportAL,
            updated_at: Date.now(),
            updated_from: "web",
        };
        if (user) PostAction.updatePost(user, updated, dispatch);
    };

    if (updatePostSuccess || deletePostSuccess) {
        PostAction.clearState(dispatch);
        navigate("/my-posts");
        return null;
    }

    if (!item) {
        return (
            <div className="min-h-screen bg-[#f8f2ed]">
                <Navbar />
                <div className="p-4">Shpallja nuk u gjet.</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f8f2ed] font-manrope">
            <Navbar />
            <div className="max-w-xl mx-auto px-4 py-6">
                <h1 className="text-2xl font-semibold text-[#704e37] mb-4">Ndrysho shpalljen</h1>
                <div className="bg-white rounded-xl shadow p-4 space-y-3">
                    <input placeholder="Emri" value={name} onChange={(e) => setName(e.target.value)} className="w-full border rounded px-3 py-2" />
                    <textarea placeholder="Përshkrimi" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border rounded px-3 py-2" rows={3} />
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Lloji</label>
                        <select value={type} onChange={(e) => setType(e.target.value as ClothesTypes)} className="w-full border rounded px-3 py-2">
                            {categoryList.map((t) => (
                                <option key={t} value={t}>{categoryLabels[t]}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Madhësitë</label>
                        <div className="flex flex-wrap gap-2">
                            {sizeOptions.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => toggleSize(s)}
                                    className={`min-w-[2.5rem] px-3 py-2 rounded-lg border-2 font-medium transition ${sizes.includes(s) ? "bg-[#704e37] border-[#704e37] text-white" : "bg-white border-gray-200 text-gray-700 hover:border-gray-400"}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Gjinia</label>
                        <select value={gender} onChange={(e) => setGender(e.target.value as ClothesGender)} className="w-full border rounded px-3 py-2">
                            <option value="female">Femër</option>
                            <option value="male">Mashkull</option>
                            <option value="both">Të dyja</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Çmimi shitje (€)</label>
                        <input type="number" min={0} value={sellPrice || ""} onChange={(e) => setSellPrice(Number(e.target.value) || 0)} className="w-full border rounded px-3 py-2" />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Çmimi qira (€)</label>
                        <input type="number" min={0} value={rentPrice || ""} onChange={(e) => setRentPrice(Number(e.target.value) || 0)} className="w-full border rounded px-3 py-2" />
                    </div>
                    <div className="flex items-center gap-2">
                        <input type="checkbox" id="free" checked={isFree} onChange={(e) => setIsFree(e.target.checked)} />
                        <label htmlFor="free">Falas</label>
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Ngjyra</label>
                        <div className="flex flex-wrap gap-1.5">
                            {colorOptions.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => toggleColor(c)}
                                    title={colorLabels[c]}
                                    className={`w-8 h-8 rounded-full border-2 transition ${colors.includes(c) ? "border-[#704e37] ring-2 ring-[#704e37]/30" : "border-gray-200 hover:border-gray-400"}`}
                                    style={{ backgroundColor: colorHex[c] }}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <label><input type="checkbox" checked={isTransportXK} onChange={(e) => setIsTransportXK(e.target.checked)} /> Kosovë</label>
                        <label><input type="checkbox" checked={isTransportMK} onChange={(e) => setIsTransportMK(e.target.checked)} /> Maqedoni</label>
                        <label><input type="checkbox" checked={isTransportAL} onChange={(e) => setIsTransportAL(e.target.checked)} /> Shqipëri</label>
                    </div>
                </div>
                {error && <p className="text-red-500 mt-2">{error}</p>}
                <div className="mt-4 flex flex-wrap gap-2">
                    <button onClick={handleSubmit} disabled={isLoading || !name.trim()} className="flex-1 min-w-[6rem] py-3 rounded-lg bg-[#704e37] text-white font-medium disabled:opacity-50">
                        {isLoading ? "Duke ruajtur..." : "Ruaj"}
                    </button>
                    <button onClick={() => navigate("/my-posts")} className="px-4 py-3 rounded-lg border border-gray-300">Anulo</button>
                    <button
                        type="button"
                        onClick={handleDeleteClick}
                        disabled={isLoading}
                        className="px-4 py-3 rounded-lg border border-red-300 bg-red-50 text-red-700 font-medium hover:bg-red-100 disabled:opacity-50"
                    >
                        Fshi shpalljen
                    </button>
                </div>
            </div>

            {/* Warning: cannot delete */}
            {showDeleteWarning && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowDeleteWarning(false)}>
                    <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Rikujtim</h3>
                        <p className="text-gray-600 text-sm whitespace-pre-line mb-4">
                            Ju nuk mund të fshini shpalljen tuaj.{"\n\n"}
                            Arsyeja mund të jetë një nga këto:{"\n\n"}
                            1. Ju nuk keni bërë porosi për këtë shpallje.{"\n"}
                            2. Shpallja ende nuk ka kaluar 50 ditë pas publikimit.{"\n\n"}
                            Nëse mendoni që ky është një gabim, na kontaktoni në njërin nga kontaktet tona.
                        </p>
                        <button type="button" onClick={() => setShowDeleteWarning(false)} className="w-full py-2.5 rounded-lg bg-[#704e37] text-white font-medium">
                            Mbyll
                        </button>
                    </div>
                </div>
            )}

            {/* Confirm delete */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Fshij shpalljen</h3>
                        <p className="text-gray-600 text-sm mb-4">Dëshironi të fshini shpalljen tuaj?</p>
                        <div className="flex gap-2">
                            <button type="button" onClick={() => setShowDeleteConfirm(false)} className="flex-1 py-2.5 rounded-lg border border-gray-300 font-medium">
                                Jo
                            </button>
                            <button type="button" onClick={handleDeleteConfirm} className="flex-1 py-2.5 rounded-lg bg-[#704e37] text-white font-medium">
                                Po
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyPostEditPage;
